/**
 * WarehouseEditModal — full-detail edit for a published warehouse item.
 *
 * The price worklist keeps its fast inline price box; tapping a row opens
 * this for everything else: title, brand, price/MSRP, Costco item #, UPC,
 * channel, and the Russian description. The description can be (re)generated
 * by pasting Costco's English copy and hitting Translate — richer + accurate
 * vs. guessing from the title (POST /ai/quick-description with sourceDescription).
 *
 * Saves via PATCH /posts/:id (which accepts every field here, incl. moving
 * channels). Mobile: full-height sheet, ≥44px targets, 16px inputs.
 */
import { useState, useRef, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Mono } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";
import { WAREHOUSE_CHANNELS } from "./warehouseChannels.js";
import { thumbUrl } from "../media.js";

// Camera barcode scanner — lazy so its ZXing chunk only loads on first scan.
const BarcodeScanner = lazy(() =>
  import("./BarcodeScanner.jsx").then((m) => ({ default: m.BarcodeScanner })));

const usd = (c) => (c == null ? "" : (c / 100).toFixed(2));
const toCents = (s) => {
  if (s === "" || s == null) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};

export function WarehouseEditModal({ post, onClose, onSaved }) {
  const t = useT();
  const toast = useToast();

  const [title, setTitle] = useState(post.title || "");
  const [brand, setBrand] = useState(post.brand || "");
  const [priceUsd, setPriceUsd] = useState(usd(post.priceCents));
  const [msrpUsd, setMsrpUsd] = useState(usd(post.originalPriceCents));
  const [itemNumber, setItemNumber] = useState(post.itemNumber || "");
  const [upc, setUpc] = useState(post.upc || "");
  const [channelSlug, setChannelSlug] = useState(post.channel?.slug || "");
  const [saleValidThrough, setSaleValidThrough] = useState(post.saleValidThrough || "");
  const [description, setDescription] = useState(post.bodyRu || "");
  const [descriptionEs, setDescriptionEs] = useState(post.bodyEs || "");
  // The English copy is the canonical source AND the saved English description
  // (body_en). Prefill from the post so re-opening shows what's stored.
  const [sourceEn, setSourceEn] = useState(post.bodyEn || "");
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Photos — view/enlarge/delete/add. Mirrors the quick-add uploader; the
  // image set is sent on save and replaces the post's photos server-side.
  const [images, setImages] = useState(
    (post.images || []).map((im) => ({ path: im.path, url: im.url })));
  const [uploading, setUploading] = useState(false);
  const [zoom, setZoom] = useState(null);          // url being enlarged, or null
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  async function addPhotos(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";                            // re-pick same file fires onChange
    if (!files.length) return;
    setUploading(true);
    try {
      const up = [];
      for (const f of files) {
        const r = await api.uploadImage(f);
        up.push({ path: r.path, url: r.url });
      }
      setImages((prev) => [...prev, ...up].slice(0, 10));
    } catch (err) {
      toast.error(t("quickAdd.upload.failed", { err: err.message || String(err) }));
    }
    setUploading(false);
  }
  const removePhoto = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  // Barcode scan → fills UPC (product EAN/UPC) or item # (Code-128 shelf tag),
  // same routing as the quick-add page.
  const [scanning, setScanning] = useState(false);
  const handleScan = (text, category) => {
    setScanning(false);
    const code = (text || "").trim();
    if (!code) return;
    if (category === "product") { setUpc(code); toast.success(t("scan.filled.barcode", { code })); }
    else { setItemNumber(code); toast.success(t("scan.filled.item", { code })); }
  };

  // ── Drag-the-header-down to dismiss ──────────────────────────────
  // The sheet only closes when the swipe STARTS on the header/grab area;
  // the scrollable body below is a normal scroll container and never
  // dismisses. dy translates the whole panel; release past a threshold
  // (or with a quick flick) closes, otherwise it snaps back.
  const [dy, setDy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const headerDrag = {
    onTouchStart: (e) => {
      dragRef.current = { y0: e.touches[0].clientY, t0: Date.now() };
      setDragging(true);
    },
    onTouchMove: (e) => {
      if (!dragRef.current) return;
      setDy(Math.max(0, e.touches[0].clientY - dragRef.current.y0));
    },
    onTouchEnd: (e) => {
      const s = dragRef.current;
      dragRef.current = null;
      setDragging(false);
      if (!s) return;
      const d = (e.changedTouches[0]?.clientY ?? s.y0) - s.y0;
      const v = d / Math.max(1, Date.now() - s.t0);   // px/ms
      if (d > 120 || v > 0.5) onClose();
      else setDy(0);
    },
  };

  const inp = {
    width: "100%", minHeight: 44, padding: "8px 10px", fontSize: 16,
    fontFamily: T_NEW.fontMono, color: T_NEW.ink, background: T_NEW.paper,
    border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r2, boxSizing: "border-box",
  };
  const label = (k) => (
    <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>{t(k)}</Mono>
  );

  // Channel options — the warehouse set, plus the current channel if it
  // somehow isn't in it (so we never blank the selection).
  const channelOpts = WAREHOUSE_CHANNELS.some(c => c.slug === channelSlug)
    ? WAREHOUSE_CHANNELS
    : [{ slug: channelSlug, label: post.channel?.name || channelSlug }, ...WAREHOUSE_CHANNELS];

  async function translate() {
    if (translating || !(sourceEn.trim() || title.trim())) return;
    setTranslating(true);
    try {
      const r = await api.generateQuickDescription({
        brand: brand.trim(), title: title.trim(),
        sourceDescription: sourceEn.trim(), channelSlug,
      });
      if (r?.descriptionRu) setDescription(r.descriptionRu);
      if (r?.descriptionEs) setDescriptionEs(r.descriptionEs);
    } catch (e) { toast.error(e.message || String(e)); }
    setTranslating(false);
  }

  async function save(thenPublish = false) {
    if (saving) return;
    setSaving(true);
    try {
      const patch = {
        title: title.trim(),
        bodyRu: description.trim() || title.trim(),
        bodyEs: descriptionEs.trim() || null,
        bodyEn: sourceEn.trim() || null,
        // Preserve the post's display mode. Operator/import photos are
        // showText=true (render title/price/desc as live text); Nordstrom Rack
        // baked cards are showText=false (text is in the image) and must stay
        // that way or the feed would duplicate it below the card.
        showText: post.showText !== false,
        brand: brand.trim() || null,
        priceCents: toCents(priceUsd),
        originalPriceCents: toCents(msrpUsd),
        saleValidThrough: saleValidThrough || null,
        itemNumber: itemNumber.trim() || null,
        upc: upc.trim() || null,
        images: images.map((im) => im.path),
      };
      const channelChanged = channelSlug && channelSlug !== post.channel?.slug;
      if (channelChanged) patch.channelSlug = channelSlug;
      await api.patchPost(post.id, patch);
      // Imports land as drafts; "Save & publish" makes them customer-visible.
      let status = post.status;
      if (thenPublish && post.status !== "published") {
        await api.publishPost(post.id);
        status = "published";
      }
      toast.success(thenPublish ? t("edit.published") : t("edit.saved"));
      onSaved?.({ id: post.id, patch, channelChanged, status });
    } catch (e) {
      toast.error(e.message || String(e));
      setSaving(false);
    }
  }

  // Portal to <body>: this modal is rendered inside the "Quick add" bottom
  // sheet, which has a CSS transform (making position:fixed relative to it,
  // not the viewport) AND a body-wide swipe-to-dismiss that would swallow
  // scroll/close touches. Portaling out makes it an independent bottom sheet:
  // the body scrolls normally and ONLY a downward drag started on the header
  // dismisses it.
  return createPortal(
    <>
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 9998, overflow: "hidden",
                  maxWidth: "100vw",
                  background: `rgba(20,17,13,${dragging ? Math.max(0.05, 0.45 - dy / 900) : 0.45})`,
                  transition: dragging ? "none" : "background-color 180ms",
                  display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()}
           style={{
             background: T_NEW.paper, borderRadius: "16px 16px 0 0",
             maxHeight: "92dvh", width: "100%", boxSizing: "border-box",
             overflowX: "hidden", display: "flex", flexDirection: "column",
             transform: `translateY(${dy}px)`,
             transition: dragging ? "none" : "transform 220ms cubic-bezier(0.2,0,0,1)",
             willChange: dragging ? "transform" : "auto" }}>
      {/* Header — the ONLY drag-to-dismiss zone; grab handle is the affordance */}
      <div {...headerDrag}
           style={{ flex: "0 0 auto", padding: "8px 16px 12px",
                    borderBottom: `1px solid ${T_NEW.ink12}`, touchAction: "none",
                    cursor: "grab" }}>
        <div aria-hidden style={{ width: 40, height: 5, borderRadius: 3,
                    background: T_NEW.ink38, margin: "2px auto 10px" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong style={{ fontSize: T_NEW.body, color: T_NEW.ink }}>{t("edit.title")}</strong>
          <button onClick={onClose} aria-label={t("edit.cancel")}
            style={{ border: "none", background: "transparent", fontSize: 22,
                     color: T_NEW.ink56, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
      </div>

      {/* Scrollable form — touchAction pan-y blocks horizontal swipe gestures
          outright, so a stray wide child can never be panned sideways. */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", touchAction: "pan-y",
                    WebkitOverflowScrolling: "touch", overscrollBehavior: "contain",
                    padding: 16, boxSizing: "border-box", minWidth: 0,
                    display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Photos — tap to enlarge, × to delete, buttons to add. First = cover. */}
        <div>
          {label("edit.photos")}
          {images.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              {images.map((im, i) => (
                <div key={im.path + i} style={{ position: "relative", width: 72, height: 72 }}>
                  <img src={thumbUrl(im.url, 240)} alt="" loading="lazy" onClick={() => setZoom(im.url)}
                       style={{ width: "100%", height: "100%", objectFit: "cover",
                                borderRadius: T_NEW.r1, border: `1px solid ${T_NEW.ink12}`,
                                cursor: "zoom-in" }} />
                  <button type="button" onClick={() => removePhoto(i)} aria-label="×"
                    style={{ position: "absolute", top: -7, right: -7, width: 22, height: 22,
                             borderRadius: "50%", border: "none", background: T_NEW.ink,
                             color: "#fff", fontSize: 15, lineHeight: "22px", cursor: "pointer",
                             padding: 0 }}>×</button>
                </div>
              ))}
            </div>
          )}
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                 multiple onChange={addPhotos} style={{ display: "none" }} />
          <input ref={libraryInputRef} type="file" accept="image/*"
                 multiple onChange={addPhotos} style={{ display: "none" }} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn variant="outline" disabled={uploading || images.length >= 10}
                 onClick={() => cameraInputRef.current?.click()}
                 style={{ flex: 1, minHeight: 40 }}>
              📷 {t("quickAdd.camera")}
            </Btn>
            <Btn variant="outline" disabled={uploading || images.length >= 10}
                 onClick={() => libraryInputRef.current?.click()}
                 style={{ flex: 1, minHeight: 40 }}>
              🖼 {t("quickAdd.library")}
            </Btn>
          </div>
          {uploading && (
            <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, marginTop: 6 }}>
              {t("quickAdd.uploading")}
            </p>
          )}
        </div>
        <div>{label("quickAdd.title")}
          <input value={title} onChange={e => setTitle(e.target.value)} style={inp} />
        </div>
        <div>{label("quickAdd.brand")}
          <input value={brand} onChange={e => setBrand(e.target.value)}
                 autoCapitalize="words" style={inp} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10 }}>
          <div>{label("quickAdd.priceUsd")}
            <input type="number" inputMode="decimal" step="0.01" min="0"
                   value={priceUsd} onChange={e => setPriceUsd(e.target.value)} style={inp} />
          </div>
          <div>{label("quickAdd.msrpUsd")}
            <input type="number" inputMode="decimal" step="0.01" min="0"
                   value={msrpUsd} onChange={e => setMsrpUsd(e.target.value)} style={inp} />
          </div>
        </div>
        <div>{label("edit.validThrough")}
          <input type="date" value={saleValidThrough}
                 onChange={e => setSaleValidThrough(e.target.value)} style={inp} />
        </div>
        <div>{label("edit.channel")}
          <select value={channelSlug} onChange={e => setChannelSlug(e.target.value)}
                  style={{ ...inp, appearance: "auto" }}>
            {channelOpts.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
        </div>
        <div>{label("quickAdd.itemNumber")}
          <input value={itemNumber} onChange={e => setItemNumber(e.target.value)}
                 inputMode="numeric" style={inp} />
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 8, marginBottom: 6 }}>
            <Mono color={T_NEW.ink56}>{t("quickAdd.upc")}</Mono>
            <Btn variant="outline" onClick={() => setScanning(true)}
                 style={{ flex: "0 0 auto", minHeight: 32, padding: "4px 10px", fontSize: T_NEW.meta }}>
              📷 {t("quickAdd.scan")}
            </Btn>
          </div>
          <input value={upc} onChange={e => setUpc(e.target.value)}
                 inputMode="numeric" style={inp} />
        </div>

        {/* Paste Costco English → Translate → fills the RU description */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 8, marginBottom: 6 }}>
            <Mono color={T_NEW.ink56} style={{ minWidth: 0, overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t("edit.source")}</Mono>
            <Btn variant="outline" disabled={translating || !(sourceEn.trim() || title.trim())}
                 onClick={translate}
                 style={{ flex: "0 0 auto", minHeight: 36, padding: "6px 12px", fontSize: T_NEW.meta }}>
              {translating ? t("edit.translating") : "✨ " + t("edit.translate")}
            </Btn>
          </div>
          <textarea value={sourceEn} onChange={e => setSourceEn(e.target.value)}
                    rows={3} placeholder={t("edit.source.placeholder")}
                    style={{ ...inp, resize: "vertical", minHeight: 70, lineHeight: 1.5 }} />
        </div>
        <div>{label("quickAdd.description")}
          <textarea value={description} onChange={e => setDescription(e.target.value)}
                    rows={3} style={{ ...inp, resize: "vertical", minHeight: 80, lineHeight: 1.5 }} />
        </div>
        <div>{label("edit.descriptionEs")}
          <textarea value={descriptionEs} onChange={e => setDescriptionEs(e.target.value)}
                    rows={3} style={{ ...inp, resize: "vertical", minHeight: 80, lineHeight: 1.5 }} />
        </div>
      </div>

      {/* Sticky actions */}
      <div style={{ display: "flex", gap: 10, padding: "12px 16px",
                    borderTop: `1px solid ${T_NEW.ink12}`, background: T_NEW.paper }}>
        <Btn variant="outline" onClick={onClose} style={{ flex: "0 0 auto", minHeight: 48, padding: "0 14px" }}>
          {t("edit.cancel")}
        </Btn>
        {post.status !== "published" ? (
          <>
            {/* Draft import: "Save" keeps it a draft, "Save & publish" goes live. */}
            <Btn variant="outline" disabled={saving} onClick={() => save(false)}
                 style={{ flex: "0 0 auto", minHeight: 48, padding: "0 14px" }}>
              {saving ? t("edit.saving") : t("edit.save")}
            </Btn>
            <Btn variant="accent" disabled={saving} onClick={() => save(true)}
                 style={{ flex: 1, minHeight: 48, fontSize: 16, fontWeight: 600 }}>
              {saving ? t("edit.saving") : t("edit.savePublish")}
            </Btn>
          </>
        ) : (
          <Btn variant="accent" disabled={saving} onClick={() => save(false)}
               style={{ flex: 1, minHeight: 48, fontSize: 16, fontWeight: 600 }}>
            {saving ? t("edit.saving") : t("edit.save")}
          </Btn>
        )}
      </div>
      </div>
    </div>
    {/* Enlarge overlay — sibling of the sheet so its taps don't close it. */}
    {zoom && (
      <div onClick={() => setZoom(null)}
           style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.92)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 16, cursor: "zoom-out" }}>
        <img src={zoom} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
      </div>
    )}
    {/* Camera barcode scanner — full-screen overlay (z 9999, above the sheet). */}
    {scanning && (
      <Suspense fallback={
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#000",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: T_NEW.meta, fontFamily: T_NEW.fontMono }}>
          {t("scan.starting")}
        </div>}>
        <BarcodeScanner onResult={handleScan} onClose={() => setScanning(false)} />
      </Suspense>
    )}
    </>,
    document.body,
  );
}
